/**
 * Recipe-component builder (Spec 016).
 *
 * Edits a Material's `recipe_components[]` — each row references a
 * child material or labor task, a `qty_per_parent_unit`, and a unit.
 * The component dropdown is constrained to real catalog material IDs
 * + labor task IDs so an invalid reference can't be constructed.
 */

import type {
  MaterialUnitKind,
  RecipeComponent,
} from "@starz/shared/api/types.gen";

const UNITS: MaterialUnitKind[] = ["sqft", "linear_ft", "each", "gallon"];

interface Props {
  components: RecipeComponent[];
  /** All catalog material IDs — valid recipe component targets. */
  materialIds: string[];
  /** All labor task IDs — also valid recipe component targets. */
  laborTaskIds: string[];
  onChange: (components: RecipeComponent[]) => void;
}

export function RecipeBuilder({
  components,
  materialIds,
  laborTaskIds,
  onChange,
}: Props) {
  function updateRow(idx: number, patch: Partial<RecipeComponent>) {
    onChange(components.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function addRow() {
    onChange([
      ...components,
      { component_material_id: "", qty_per_parent_unit: 1, unit: "sqft" },
    ]);
  }

  function removeRow(idx: number) {
    onChange(components.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Recipe components
        </label>
        <button
          type="button"
          onClick={addRow}
          className="rounded-md border border-starz-gold/30 px-3 py-1 text-xs font-medium text-starz-navy hover:bg-starz-cream"
        >
          + Add component
        </button>
      </div>

      {components.length === 0 ? (
        <p className="text-xs text-warm-gray">
          No recipe components. Itemized materials expand into a recipe of
          child materials + labor; bundled materials leave this empty.
        </p>
      ) : (
        <div className="space-y-2">
          {components.map((c, idx) => (
            <div
              key={idx}
              className="flex flex-wrap items-end gap-2 rounded-lg border border-starz-gold/15 bg-starz-cream/40 p-3"
            >
              <div className="flex-1 min-w-[180px]">
                <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
                  Component
                </label>
                <select
                  value={c.component_material_id}
                  onChange={(e) =>
                    updateRow(idx, { component_material_id: e.target.value })
                  }
                  className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">— select —</option>
                  <optgroup label="Materials">
                    {materialIds.map((id) => (
                      <option key={`m-${id}`} value={id}>
                        {id}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Labor tasks">
                    {laborTaskIds.map((id) => (
                      <option key={`l-${id}`} value={id}>
                        {id}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div className="w-28">
                <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
                  Qty / parent
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={c.qty_per_parent_unit}
                  onChange={(e) =>
                    updateRow(idx, {
                      qty_per_parent_unit: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
                />
              </div>
              <div className="w-28">
                <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
                  Unit
                </label>
                <select
                  value={c.unit}
                  onChange={(e) =>
                    updateRow(idx, { unit: e.target.value as MaterialUnitKind })
                  }
                  className="mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm"
                >
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="rounded-md border border-starz-brick/30 px-2 py-1.5 text-xs text-starz-brick hover:bg-starz-brick/5"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
